// Drawer para que el profe rellene/edite el feedback de un alumno tras la clase.
// Marcar "Completado" dispara el email de aviso al alumno.

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { StarRating } from '@/components/molecules/StarRating';
import { ApiError } from '@/services/api';
import {
  getFeedback,
  upsertFeedback,
  type Attendance,
  type Feedback,
} from '@/services/feedbacks';
import styles from './FeedbackEditorDrawer.module.scss';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: number;
  studentId: number;
  studentName: string;
  lessonTitle: string | null;
  scheduledAt: string;
  onSaved: () => void;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }) +
    ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

export function FeedbackEditorDrawer({
  open,
  onOpenChange,
  bookingId,
  studentId,
  studentName,
  lessonTitle,
  scheduledAt,
  onSaved,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const [attendance, setAttendance] = useState<Attendance | null>(null);
  const [pronunciation, setPronunciation] = useState<number | null>(null);
  const [fluency, setFluency] = useState<number | null>(null);
  const [structures, setStructures] = useState<number | null>(null);
  const [vocabulary, setVocabulary] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFeedback(bookingId, studentId)
      .then((f) => {
        if (cancelled) return;
        setFeedback(f);
        setAttendance(f?.attendance ?? null);
        setPronunciation(f?.pronunciation ?? null);
        setFluency(f?.fluency ?? null);
        setStructures(f?.structures ?? null);
        setVocabulary(f?.vocabulary ?? null);
        setNotes(f?.notes ?? '');
      })
      .catch((e) => !cancelled && setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, bookingId, studentId]);

  async function save(markComplete: boolean) {
    setBusy(true);
    setError(null);
    try {
      const updated = await upsertFeedback(bookingId, studentId, {
        attendance,
        pronunciation,
        fluency,
        structures,
        vocabulary,
        notes: notes.trim() || null,
        is_complete: markComplete,
      });
      setFeedback(updated);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  }

  const isComplete = feedback?.is_complete === 1;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.drawer} aria-describedby={undefined}>
          <header className={styles.header}>
            <div className={styles.headerLeft}>
              <Dialog.Title className={styles.titleHeader}>Feedback de {studentName}</Dialog.Title>
              <p className={styles.subtitle}>
                {formatDate(scheduledAt)}
                {lessonTitle && ` · ${lessonTitle}`}
              </p>
            </div>
            <Dialog.Close asChild>
              <button type="button" className={styles.closeBtn} aria-label="Cerrar">
                <Icon name="x" size={16} />
              </button>
            </Dialog.Close>
          </header>

          {loading ? (
            <div className={styles.center}><Spinner size="lg" /></div>
          ) : (
            <div className={styles.body}>
              {isComplete && (
                <p className={styles.alreadyComplete}>
                  <Badge tone="success">Completado</Badge>
                  Este feedback ya está publicado. El alumno lo puede ver. Tus cambios sobrescriben el publicado.
                </p>
              )}

              <div className={styles.field}>
                <span>Asistencia</span>
                <div className={styles.attendanceGroup}>
                  {(['present', 'late', 'absent'] as Attendance[]).map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={`${styles.attendanceBtn} ${attendance === opt ? styles.attendanceActive : ''}`}
                      onClick={() => setAttendance(attendance === opt ? null : opt)}
                    >
                      {opt === 'present' ? 'Presente' : opt === 'late' ? 'Tarde' : 'Ausente'}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.scoreRow}>
                <span className={styles.scoreLabel}>Pronunciation</span>
                <StarRating value={pronunciation} onChange={setPronunciation} size={22} label="Pronunciation" />
              </div>
              <div className={styles.scoreRow}>
                <span className={styles.scoreLabel}>Fluency</span>
                <StarRating value={fluency} onChange={setFluency} size={22} label="Fluency" />
              </div>
              <div className={styles.scoreRow}>
                <span className={styles.scoreLabel}>Structures</span>
                <StarRating value={structures} onChange={setStructures} size={22} label="Structures" />
              </div>
              <div className={styles.scoreRow}>
                <span className={styles.scoreLabel}>Vocabulary</span>
                <StarRating value={vocabulary} onChange={setVocabulary} size={22} label="Vocabulary" />
              </div>

              <label className={styles.field}>
                <span>Notas para el alumno</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  placeholder="Comentarios, áreas de mejora, recomendaciones…"
                />
              </label>
            </div>
          )}

          {error && <p className={styles.error}>{error}</p>}

          <footer className={styles.footer}>
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancelar
            </Button>
            <div className={styles.footerRight}>
              <Button variant="secondary" onClick={() => save(false)} disabled={busy || loading}>
                {busy ? 'Guardando…' : 'Guardar borrador'}
              </Button>
              <Button variant="primary" onClick={() => save(true)} disabled={busy || loading}>
                {busy ? 'Guardando…' : isComplete ? 'Volver a publicar' : 'Publicar y avisar al alumno'}
              </Button>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
